/**
 * Journey: Sperm Freezing, Solo, Male
 *
 * Steps: Learn > Test > Freeze
 * Includes optimization window for the 74-day sperm regeneration cycle.
 */

import type { JourneyDefinition, PlanStep } from "../plan.types";

const learnStep: PlanStep = {
  id: "learn",
  title: "How sperm freezing works",
  subtitle: "The simplest fertility preservation option",
  icon: "BookOpen",
  education: {
    context:
      "Sperm freezing (cryopreservation) is the simplest fertility preservation option. You provide semen samples, they're analyzed for quality, processed with cryoprotectant, and stored in liquid nitrogen at -196°C indefinitely.",
    details:
      "Most men bank 2-3 samples across separate visits, spaced 2-3 days apart. This provides redundancy and enough material for multiple future procedures:\n• IUI (intrauterine insemination) uses ~1 vial per attempt\n• IVF/ICSI uses ~1 vial per cycle\n• Banking 6-10 vials is typical\n\nCost: $500-$1,000 for analysis + processing + first year storage, then $300-500/year\nTime: 2-3 visits over 1-2 weeks",
    learnMoreLink: "sperm-freezing-guide",
  },
  actions: [
    {
      label: "I understand this",
      variant: "primary",
      icon: "Check",
      signal: "complete-step",
      params: { stepId: "learn" },
    },
    {
      label: "Full guide",
      variant: "outline",
      icon: "BookOpen",
      screen: "ArticleDetail",
      params: { articleId: "sperm-freezing-guide" },
    },
  ],
  completionSignal: { type: "manual" },
};

const testStep: PlanStep = {
  id: "test",
  title: "Semen analysis",
  subtitle: "Understand your baseline",
  icon: "TestTube",
  education: {
    context:
      "A semen analysis evaluates three key parameters: count (>15 million/mL is normal), motility (>40% is normal), and morphology (>4% normal forms). Results matter for freezing because not all sperm survive the freeze-thaw process (~50% motility loss is typical).",
  },
  actions: [
    {
      label: "Find a lab near you",
      variant: "primary",
      icon: "MapPin",
      screen: "FindCareScreen",
      params: { filter: "sperm-freezing" },
    },
    {
      label: "Upload SA results",
      variant: "outline",
      icon: "Upload",
      screen: "DocumentsScreen",
    },
    {
      label: "Supplements for sperm quality",
      variant: "secondary",
      icon: "Pill",
      screen: "SupplementsScreen",
      params: { goal: "sperm-health" },
    },
  ],
  substeps: [
    {
      id: "find-lab",
      title: "Find a lab or clinic",
      education:
        "Most fertility clinics and many urology offices offer SA. At-home kits exist but clinic-grade analysis is more reliable for freezing decisions.",
      status: "not-started",
    },
    {
      id: "get-analysis",
      title: "Get your analysis done",
      education:
        "Abstain 2-5 days before your appointment. Results are usually available same day or next day.",
      status: "not-started",
    },
    {
      id: "upload-results",
      title: "Upload results",
      education:
        "Having your results on file helps your clinic plan if you need ICSI or other interventions.",
      status: "not-started",
      actions: [
        {
          label: "Upload results",
          variant: "primary",
          screen: "DocumentsScreen",
        },
      ],
    },
  ],
  completionSignal: { type: "test-result-exists", testIds: ["partner-sa"] },
  optimizationWindow: {
    substepId: "get-analysis",
    biologicalCycle: "~74 days for full sperm regeneration",
    baselineAction: "Get a baseline SA",
    optimizationPeriod: "2-3 months",
    retestAction: "Retest to see improvement",
    lifestyle: [
      {
        title: "Avoid excessive heat",
        evidence: "strong",
        impact: "high",
        education:
          "Sperm production requires temperatures 2-4°C below body temperature. Hot tubs, saunas, tight underwear, and laptops on your lap all raise scrotal temperature. Even brief heat exposure can reduce counts for weeks.",
      },
      {
        title: "Limit alcohol",
        evidence: "strong",
        impact: "moderate",
        education:
          "Heavy alcohol use reduces testosterone and impairs sperm production. Moderate drinking (1-2 drinks/day) shows mixed evidence, but limiting intake during your optimization window is prudent.",
      },
      {
        title: "Exercise regularly",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Regular moderate exercise improves sperm parameters. However, extreme endurance exercise (marathon training, triathlons) and heavy cycling can temporarily lower counts.",
      },
      {
        title: "Maintain healthy weight",
        evidence: "strong",
        impact: "high",
        education:
          "Obesity significantly impacts sperm count and quality through hormonal disruption (increased estrogen, decreased testosterone). Even modest weight loss can improve parameters.",
      },
      {
        title: "Quit smoking/vaping",
        evidence: "strong",
        impact: "high",
        education:
          "Tobacco reduces sperm count by 20-30% and increases DNA fragmentation. Vaping has similar effects. Cannabis also impairs motility and morphology.",
      },
    ],
    supplements: [
      {
        title: "CoQ10 (200-400mg/day)",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Supports mitochondrial function in sperm cells. Multiple studies show improvements in count and motility after 3 months of supplementation.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
      {
        title: "Zinc (30mg/day)",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Essential for testosterone production and sperm development. Seminal fluid has 100x the zinc concentration of blood. Deficiency directly impacts quality.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
      {
        title: "Folate/L-methylfolate (400-800mcg/day)",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Supports DNA synthesis in developing sperm. Combined with zinc, studies show improved count.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
      {
        title: "Vitamin D (2000-4000 IU/day)",
        evidence: "moderate",
        impact: "low",
        education:
          "Vitamin D receptors exist on sperm cells. Deficiency is associated with lower motility.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
    ],
    ageGuidance: [
      {
        bracket: "Under 35",
        range: [0, 34],
        recommendation: "optimize-then-start",
        explanation:
          "You have time. A full 3-month optimization window can meaningfully improve your baseline before banking. Get a baseline SA now, start supplements and lifestyle changes, retest at 10-12 weeks.",
      },
      {
        bracket: "35-40",
        range: [35, 40],
        recommendation: "parallel",
        explanation:
          "Bank a baseline sample now for insurance, then optimize and retest. If improved, bank additional optimized samples. You're preserving against future decline. Starting matters.",
      },
      {
        bracket: "Over 40",
        range: [41, 99],
        recommendation: "start-now-optimize-alongside",
        explanation:
          "Sperm quality declines with age: DNA fragmentation increases, motility decreases. Bank samples now. Continue optimizing but don't delay banking for a perfect baseline.",
      },
    ],
  },
};

const freezeStep: PlanStep = {
  id: "freeze",
  title: "Bank your samples",
  subtitle: "2-3 visits over 1-2 weeks",
  icon: "Snowflake",
  education: {
    context:
      "Your clinic or lab will guide you through the collection process. Abstain 2-5 days before each collection (longer isn't better). Collection is usually done on-site in a private room.",
    details:
      "Each sample is analyzed, processed with cryoprotectant, divided into vials, and slowly frozen. You'll receive a report for each sample with pre-freeze and post-thaw quality.",
  },
  actions: [
    {
      label: "Set my start date",
      variant: "primary",
      icon: "Calendar",
      signal: "open-start-treatment-modal",
      params: { presetCycleType: "sperm_freezing" },
    },
  ],
  substeps: [
    {
      id: "schedule-collections",
      title: "Schedule collection appointments (2-3 visits)",
      education: "Space visits 2-3 days apart for optimal sample quality.",
      status: "not-started",
    },
    {
      id: "collection-1",
      title: "First collection",
      education:
        "Abstain 2-5 days before. The lab will analyze and freeze your sample.",
      status: "not-started",
    },
    {
      id: "collection-2",
      title: "Second collection",
      education: "Space 2-3 days after first collection.",
      status: "not-started",
    },
    {
      id: "collection-3",
      title: "Third collection (optional)",
      education: "Additional samples provide more redundancy for future use.",
      status: "not-started",
    },
    {
      id: "confirm-storage",
      title: "Confirm storage arrangement and payment",
      education: "Set up annual storage payment. Most labs offer auto-renewal.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "external" },
};

export const SPERM_FREEZING_SOLO: JourneyDefinition = {
  key: "sperm-freezing-solo",
  gender: "male",
  goal: "preserve",
  pathway: "solo",
  title: "Sperm Freezing",
  description: "Preserve your sperm for future use",
  steps: [learnStep, testStep, freezeStep],
};
